import { create } from 'zustand';
import { supabase } from '../supabase';
import type { ClientRelationship } from '../types';

/**
 * Data layer per i link persona↔azienda (modello Contatti/Aziende first-class).
 * Usa la tabella `client_relationships` con relationship_type='contatto'
 * (source = persona individual, target = azienda company). Bidirezionale:
 * una sola riga letta da entrambi i lati. Vedi MD/architecture/contatti-aziende-firstclass-migrazione.md
 *
 * Soft delete: "scollega" = is_active=false (non elimina il dato).
 */

const CONTATTO = 'contatto';

// Campi del client "persona" e "azienda" da mostrare nelle sezioni
const PERSON_SELECT = 'id, business_name, entity_type, email, phone, stato_cliente';
const COMPANY_SELECT = 'id, business_name, entity_type, email, phone, city, vat_number';

async function getOrgId(): Promise<string> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('Utente non autenticato');
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('organization_id')
    .eq('id', userData.user.id)
    .single();
  if (!profile?.organization_id) throw new Error('Utente senza organizzazione');
  return profile.organization_id;
}

export interface ContactCompanyLinkInput {
  person_client_id: string;   // source (individual)
  company_client_id: string;  // target (company)
  role?: string | null;
  area?: string | null;
  is_referente?: boolean;
  is_rappresentante_legale?: boolean;
  is_primary_contact?: boolean;   // referente principale (lato azienda)
  is_primary_company?: boolean;   // azienda primaria (lato persona)
  label_ids?: string[];           // association labels (stile HubSpot)
  notes?: string | null;
}

interface ContactCompanyLinkStore {
  loading: boolean;
  error: string | null;
  /** Persone collegate a un'azienda (per la scheda Azienda) */
  fetchForCompany: (companyId: string) => Promise<ClientRelationship[]>;
  /** Aziende collegate a una persona (per la scheda Persona) */
  fetchForPerson: (personId: string) => Promise<ClientRelationship[]>;
  linkPersonToCompany: (input: ContactCompanyLinkInput) => Promise<ClientRelationship | null>;
  updateLink: (id: string, patch: Partial<ClientRelationship>) => Promise<ClientRelationship | null>;
  /** Scollega = soft delete (is_active=false) */
  unlink: (id: string) => Promise<boolean>;
  /** Imposta l'azienda primaria per una persona (unica) */
  setPrimaryCompany: (personId: string, linkId: string) => Promise<boolean>;
  /** Imposta il referente principale per un'azienda (unico) */
  setPrimaryContact: (companyId: string, linkId: string) => Promise<boolean>;
}

export const useContactCompanyLinkStore = create<ContactCompanyLinkStore>((set) => ({
  loading: false,
  error: null,

  fetchForCompany: async (companyId) => {
    set({ loading: true, error: null });
    try {
      const orgId = await getOrgId();
      const { data, error } = await supabase
        .from('client_relationships')
        .select(`*, person:source_client_id (${PERSON_SELECT})`)
        .eq('organization_id', orgId)
        .eq('relationship_type', CONTATTO)
        .eq('target_client_id', companyId)
        .eq('is_active', true)
        .order('is_primary_contact', { ascending: false })
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data as ClientRelationship[]) || [];
    } catch (e) {
      set({ error: (e as Error).message });
      return [];
    } finally {
      set({ loading: false });
    }
  },

  fetchForPerson: async (personId) => {
    set({ loading: true, error: null });
    try {
      const orgId = await getOrgId();
      const { data, error } = await supabase
        .from('client_relationships')
        .select(`*, company:target_client_id (${COMPANY_SELECT})`)
        .eq('organization_id', orgId)
        .eq('relationship_type', CONTATTO)
        .eq('source_client_id', personId)
        .eq('is_active', true)
        .order('is_primary_company', { ascending: false })
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data as ClientRelationship[]) || [];
    } catch (e) {
      set({ error: (e as Error).message });
      return [];
    } finally {
      set({ loading: false });
    }
  },

  linkPersonToCompany: async (input) => {
    try {
      const orgId = await getOrgId();
      const { data, error } = await supabase
        .from('client_relationships')
        .insert({
          source_client_id: input.person_client_id,
          target_client_id: input.company_client_id,
          relationship_type: CONTATTO,
          organization_id: orgId,
          role: input.role ?? null,
          area: input.area ?? null,
          is_referente: input.is_referente ?? false,
          is_rappresentante_legale: input.is_rappresentante_legale ?? false,
          is_primary_contact: input.is_primary_contact ?? false,
          is_primary_company: input.is_primary_company ?? false,
          label_ids: input.label_ids ?? [],
          is_active: true,
          notes: input.notes ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as ClientRelationship;
    } catch (e) {
      set({ error: (e as Error).message });
      return null;
    }
  },

  updateLink: async (id, patch) => {
    try {
      const orgId = await getOrgId();
      const { data, error } = await supabase
        .from('client_relationships')
        .update(patch)
        .eq('id', id)
        .eq('organization_id', orgId)
        .select()
        .single();
      if (error) throw error;
      return data as ClientRelationship;
    } catch (e) {
      set({ error: (e as Error).message });
      return null;
    }
  },

  unlink: async (id) => {
    try {
      const orgId = await getOrgId();
      const { error } = await supabase
        .from('client_relationships')
        .update({ is_active: false })
        .eq('id', id)
        .eq('organization_id', orgId);
      if (error) throw error;
      return true;
    } catch (e) {
      set({ error: (e as Error).message });
      return false;
    }
  },

  setPrimaryCompany: async (personId, linkId) => {
    try {
      const orgId = await getOrgId();
      // azzera gli altri primari della persona (partial-unique DB lo impone comunque)
      await supabase
        .from('client_relationships')
        .update({ is_primary_company: false })
        .eq('organization_id', orgId)
        .eq('relationship_type', CONTATTO)
        .eq('source_client_id', personId)
        .neq('id', linkId);
      const { error } = await supabase
        .from('client_relationships')
        .update({ is_primary_company: true })
        .eq('id', linkId)
        .eq('organization_id', orgId);
      if (error) throw error;
      return true;
    } catch (e) {
      set({ error: (e as Error).message });
      return false;
    }
  },

  setPrimaryContact: async (companyId, linkId) => {
    try {
      const orgId = await getOrgId();
      await supabase
        .from('client_relationships')
        .update({ is_primary_contact: false })
        .eq('organization_id', orgId)
        .eq('relationship_type', CONTATTO)
        .eq('target_client_id', companyId)
        .neq('id', linkId);
      const { error } = await supabase
        .from('client_relationships')
        .update({ is_primary_contact: true })
        .eq('id', linkId)
        .eq('organization_id', orgId);
      if (error) throw error;
      return true;
    } catch (e) {
      set({ error: (e as Error).message });
      return false;
    }
  },
}));
